'use client'

import { useBackInStockModalContext } from 'back-in-stock/src/hooks/useBackInStockModalContext/useBackInStockModalContext'
import type { BackInStockProps } from 'back-in-stock/src/types'
import { BottomSheet as FukkuBottomSheet } from 'fukku/Dialog/BottomSheet/BottomSheet'
import { Content } from 'fukku/Dialog/Content/Content'
import type { DialogAPI } from 'fukku/Dialog/Dialog'
import { Header } from 'fukku/Dialog/Header/Header'
import { Steps, type StepsApi } from 'fukku/Dialog/Steps/Steps'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import type { Product } from 'product/types'
import { type ReactNode, useEffect, useMemo, useRef } from 'react'

import { DisclaimerMessage } from '../DisclaimerMessage/DisclaimerMessage'
import { SizeSheet } from '../SizeSheet'

/* eslint react-hooks/exhaustive-deps: warn */

const DynamicBackInStockStep = dynamic(
	() =>
		import('./BackInStockStep/BackInStockStep').then(
			(mod) => mod.BackInStockStep
		),
	{ ssr: false }
)

export function BottomSheet({
	sizeSheetRef,
	product,
	onClose,
}: Readonly<{
	sizeSheetRef: React.RefObject<DialogAPI>
	product: Product
	onClose: () => void
}>) {
	const { t } = useLabels()
	const { backInStockModalProps } = useBackInStockModalContext()

	const disclaimerType = useMemo(
		() =>
			product.colors.find((color) => !!color.disclaimer)?.disclaimer ?? null,
		[product.colors]
	)

	const stepsRef = useRef<StepsApi<BackInStockProps | null>>(null)

	useEffect(() => {
		if (backInStockModalProps && stepsRef.current) {
			stepsRef.current.ctx.detail = backInStockModalProps
			stepsRef.current.next()
		}
	}, [backInStockModalProps])

	const steps = useMemo<
		(_next: () => void, prev: () => void) => [ReactNode, ReactNode]
	>(
		() => (_next, prev) => [
			<>
				<Header title={t('productCard.selectSize.title')} />
				<Content hasPadding={false}>
					{disclaimerType && <DisclaimerMessage type={disclaimerType} />}
					<SizeSheet product={product} onClose={onClose} />
				</Content>
			</>,
			<DynamicBackInStockStep key='step2' prev={prev} />,
		],
		[disclaimerType, product, onClose, t]
	)

	return (
		<FukkuBottomSheet
			data-testid='productCard.sizeSheet'
			title={t('productCard.selectSize.title')}
			ref={sizeSheetRef}
		>
			<Steps ref={stepsRef}>{steps}</Steps>
		</FukkuBottomSheet>
	)
}
